'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '@/lib/toast';
import { useParams } from 'next/navigation';
import {
  getInventoryItemTypesAction,
  InventoryItemType
} from '@/lib/services/inventory-types';
import { useCreateInventoryItem } from '@/lib/services/inventory-items';
import type { SetValueConfirmationRequest } from '@/lib/services/inventory-items/types';

type ViewState = 'type-selection' | 'form';

interface FormData {
  institution_id: string;
  estateReference: string;
  institutionName?: string;
  institutionEmail?: string;
}

export function useAddInventoryDialog(
  propMatterId?: string,
  onInventoryAdded?: () => void,
  initialInventoryType?: string,
  category?: 'asset' | 'liability'
) {
  const params = useParams();
  const matterId = propMatterId || (params['id'] as string);

  // Use the mutation hook
  const createInventoryMutation = useCreateInventoryItem(
    parseInt(matterId || '0', 10)
  );

  // View state
  const [view, setView] = useState<ViewState>('type-selection');
  const [loading, setLoading] = useState(false);

  // Data
  const [inventoryTypes, setInventoryTypes] = useState<InventoryItemType[]>([]);
  const [selectedType, setSelectedType] = useState<InventoryItemType | null>(
    null
  );
  // Form data
  const [formData, setFormData] = useState<FormData>({
    institution_id: '',
    estateReference: '',
    institutionName: '',
    institutionEmail: ''
  });
  const [dynamicFormData, setDynamicFormData] = useState<
    Record<string, unknown>
  >({});

  // Track which category we last loaded types for
  const loadedCategoryRef = useRef<string | undefined>(undefined);

  const loadInventoryTypes = useCallback(async () => {
    // Don't fetch if we already have data for this category
    if (
      inventoryTypes &&
      inventoryTypes.length > 0 &&
      loadedCategoryRef.current === category
    ) {
      return;
    }

    setLoading(true);
    try {
      const result = await getInventoryItemTypesAction(category);
      if (result.success && result.data) {
        setInventoryTypes(result.data.types || []);
        loadedCategoryRef.current = category;
      } else {
        toast.error(result.error || 'Failed to load inventory types');
        setInventoryTypes([]);
      }
    } catch (_error) {
      toast.error('Failed to load inventory types');
      setInventoryTypes([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps -- only reads `inventoryTypes.length`; depending on the whole array would invalidate this callback whenever types are reloaded.
  }, [inventoryTypes?.length, category]);

  // Load data when dialog opens
  const loadDialogData = useCallback(async () => {
    await loadInventoryTypes();
  }, [loadInventoryTypes]);

  const handleTypeSelect = useCallback(
    async (typeId: string) => {
      if (!typeId) {
        return; // No typeId provided
      }

      // Find the type from our already-loaded inventory types list
      const matchingType = inventoryTypes.find((type) => type.id === typeId);

      if (!matchingType) {
        toast.error('Inventory type not found');
        return;
      }

      // Set the type and show the record immediately. **No institutions fetch
      // rides along any more** (VALE-316 / [INVDRAW-21]): the retired
      // `InventoryFormCard` arm was the only consumer of that roster, and the
      // attribute record owns its own institution search through
      // `use-inventory-block-data.ts`.
      setSelectedType(matchingType);
      setView('form');
      setDynamicFormData({});
    },
    [inventoryTypes]
  );

  const resetDialog = useCallback(() => {
    setView('type-selection');
    setSelectedType(null);
    setInventoryTypes([]);
    loadedCategoryRef.current = undefined;
    setFormData({
      institution_id: '',
      estateReference: '',
      institutionName: '',
      institutionEmail: ''
    });
    setDynamicFormData({});
  }, []);

  const updateFormData = useCallback((field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDynamicFormChange = useCallback(
    (data: Record<string, unknown>) => {
      setDynamicFormData(data || {});
    },
    []
  );

  const handleSubmit = useCallback(
    async (dynamicData: Record<string, unknown>) => {
      if (!selectedType) return;

      if (!matterId) {
        toast.error(
          'Matter ID is missing. Please refresh the page and try again.'
        );
        return;
      }

      // Required-field validation belongs to the record itself — the add
      // dialog's Save is gated by `InventoryAttributeFormView`, not here.

      try {
        // Extract the full item from dynamicData if available (built by
        // `InventoryAttributeFormView.toSubmitData` out of the buffered record)
        const fullItem = dynamicData._fullItem as
          | {
              institution?: {
                id?: number;
                isCustom?: boolean;
                name?: string;
                email?: string;
                /** See {@link CustomInstitution.institutionType} (VALE-1577). */
                institutionType?: string;
              };
              institutionId?: number;
              isNonEstate?: boolean;
              payeeContactId?: number;
              linkedInventoryItemId?: number;
              estateReference?: string;
              /*
                Value-confirmation intents the attribute add dialog buffered
                (VALE-355). They ride in `_fullItem` like every other non-`itemInfo`
                field, and have to be lifted out onto the create payload explicitly —
                `itemInfoData` below is *everything else* in `dynamicData`, so a
                confirmation left unlifted would be posted as two junk `itemInfo`
                keys and the real columns would never be written.
              */
              currentValueConfirmation?: SetValueConfirmationRequest | null;
              dodValueConfirmation?: SetValueConfirmationRequest | null;
            }
          | undefined;

        // Handle institution field - either from fullItem or legacy formData
        let institutionId: number | undefined = undefined;
        let institutionName: string | undefined = undefined;
        let institutionEmail: string | undefined = undefined;
        /* The type chosen on the create step, where it offered one (VALE-1577). */
        let institutionType: string | undefined = undefined;

        if (fullItem?.institution) {
          const inst = fullItem.institution;
          // Handle custom institution (has id: -1 and isCustom: true)
          if ((!inst.id || inst.id === -1) && inst.isCustom) {
            institutionName = inst.name;
            institutionEmail = inst.email;
            institutionType = inst.institutionType;
          } else if (inst.id && inst.id > 0) {
            institutionId = inst.id;
          }
        } else if (fullItem?.institutionId) {
          institutionId = fullItem.institutionId;
        } else if (formData.institution_id === 'add-new') {
          // Fallback to legacy formData for custom institution
          institutionName = formData.institutionName;
          institutionEmail = formData.institutionEmail;
        } else if (formData.institution_id) {
          institutionId = parseInt(formData.institution_id);
        }

        // Remove _fullItem from the data before building itemInfo
        const { _fullItem, estateReference, ...itemInfoData } = dynamicData;

        // Create inventory item data structure matching backend expectations
        // Use institution: { id } object instead of institutionId
        const inventoryData = {
          type: selectedType.slug, // Use slug instead of name
          institution: institutionId ? { id: institutionId } : undefined,
          institutionName: institutionName,
          institutionEmail: institutionEmail,
          institutionType: institutionType,
          isNonEstate: fullItem?.isNonEstate || undefined,
          payeeContactId: fullItem?.payeeContactId || undefined,
          linkedInventoryItemId: fullItem?.linkedInventoryItemId || undefined,
          estateReference:
            (estateReference as string | undefined) ||
            fullItem?.estateReference ||
            undefined,
          itemInfo: itemInfoData || undefined, // Include the dynamic form data
          /*
            `?? undefined`, not `|| undefined`: `null` means "clear" on the update
            path and has no meaning on a create, so it must not be forwarded — the
            backend would take it as an explicit intent. Absent is the only correct
            spelling of "nothing to confirm" here (VALE-355).
          */
          currentValueConfirmation:
            fullItem?.currentValueConfirmation ?? undefined,
          dodValueConfirmation: fullItem?.dodValueConfirmation ?? undefined
        };

        // Submitting inventory data
        await createInventoryMutation.mutateAsync(inventoryData);

        // Success toast is handled by the mutation hook
        // Reset state
        resetDialog();
        // Trigger refresh callback (cache invalidation happens automatically)
        setTimeout(() => {
          onInventoryAdded?.();
        }, 100);
      } catch (_error) {
        // Error toast is handled by the mutation hook
      }
    },
    [
      selectedType,
      formData,
      matterId,
      onInventoryAdded,
      createInventoryMutation,
      resetDialog
    ]
  );

  const handleBack = useCallback(() => {
    setView('type-selection');
  }, []);

  // Auto-select initial inventory type if provided
  useEffect(() => {
    // Auto-select effect triggered

    if (
      initialInventoryType &&
      inventoryTypes.length > 0 &&
      !selectedType &&
      view === 'type-selection'
    ) {
      const matchingType = inventoryTypes.find(
        (type) =>
          type.slug === initialInventoryType ||
          type.name.toLowerCase() === initialInventoryType.toLowerCase()
      );

      // Looking for matching type

      if (matchingType) {
        // Auto-selecting type
        // eslint-disable-next-line react-hooks/set-state-in-effect -- handleTypeSelect is a stable callback invoked once on the initial-type match; the compiler flags it as a synchronous cascade it is not.
        handleTypeSelect(matchingType.id);
      } else {
        // No matching type found
      }
    }
  }, [
    initialInventoryType,
    inventoryTypes,
    selectedType,
    view,
    handleTypeSelect
  ]);

  return {
    // State
    view,
    loading,
    submitting: createInventoryMutation.isPending,
    inventoryTypes,
    selectedType,
    formData,
    dynamicFormData,

    // Actions
    loadDialogData,
    handleTypeSelect,
    updateFormData,
    setDynamicFormData: handleDynamicFormChange,
    handleSubmit,
    handleBack,
    resetDialog
  };
}
